import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type {
  IssuedKeys,
  KeyTarget,
  OpenApiPartner,
  OpenApiPartnerHistory,
  PartnerCreatePayload,
  PartnerUpdatePayload,
} from '../types/openApiPartner';

/**
 * 매출 수집 Open API 제휴사 관리(order-service AdminOpenApiPartnerController, gateway /api/admin/v1/open-api/**).
 * generated OpenAPI 에 없는 경로라 adminFetch 로 호출한다. 응답 envelope = { success, data, meta }.
 *
 * ★등록·재발급 응답의 키 원문은 쿼리 캐시에 넣지 않는다 — 목록·상세는 다시 불러온다(마스킹값만 캐시).
 *   호출부도 원문을 모달 상태에만 두고 닫을 때 버리며, mutation 결과는 받는 즉시 reset 한다(gcTime 0 이라
 *   mutation 캐시에서도 바로 지워진다).
 */
const BASE = '/api/admin/v1/open-api/partners';

export const openApiPartnersKey = ['admin', 'open-api-partners'] as const;
export const openApiPartnerKey = (id: string) => [...openApiPartnersKey, 'detail', id] as const;
export const openApiPartnerHistoriesKey = (id: string) => [...openApiPartnersKey, 'histories', id] as const;

/** BE 오류 — status(400 검증·403 권한·404 없음)와 BE 메시지. */
export class OpenApiPartnerError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
  ) {
    super(message);
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string } | null;
}

const toError = async (res: Response, fallback: string): Promise<OpenApiPartnerError> => {
  let env: ErrorEnvelope | null = null;
  try {
    env = (await res.json()) as ErrorEnvelope;
  } catch {
    env = null;
  }
  const message =
    env?.error?.message ||
    (res.status === 403 ? '권한이 없습니다.' : res.status === 404 ? '제휴사를 찾을 수 없습니다.' : fallback);
  return new OpenApiPartnerError(message, res.status, env?.error?.code ?? null);
};

const readData = async <T,>(res: Response, fallback: string): Promise<T> => {
  if (!res.ok) throw await toError(res, fallback);
  const env = (await res.json()) as { data: T };
  return env.data;
};

const normalize = (p: OpenApiPartner): OpenApiPartner => ({ ...p, allowedIps: p.allowedIps ?? [] });

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// ───────────────────────── 조회 ─────────────────────────

export const useOpenApiPartnersQuery = () =>
  useQuery<OpenApiPartner[], OpenApiPartnerError>({
    queryKey: openApiPartnersKey,
    queryFn: async () => {
      const list = await readData<OpenApiPartner[] | null>(await adminFetch(BASE), '제휴사 목록을 불러오지 못했습니다.');
      return (list ?? []).map(normalize);
    },
  });

export const useOpenApiPartnerQuery = (partnerId: string | undefined) =>
  useQuery<OpenApiPartner, OpenApiPartnerError>({
    queryKey: openApiPartnerKey(partnerId ?? ''),
    enabled: Boolean(partnerId),
    queryFn: async () =>
      normalize(
        await readData<OpenApiPartner>(
          await adminFetch(`${BASE}/${encodeURIComponent(partnerId ?? '')}`),
          '제휴사 정보를 불러오지 못했습니다.',
        ),
      ),
  });

export const useOpenApiPartnerHistoriesQuery = (partnerId: string | undefined) =>
  useQuery<OpenApiPartnerHistory[], OpenApiPartnerError>({
    queryKey: openApiPartnerHistoriesKey(partnerId ?? ''),
    enabled: Boolean(partnerId),
    queryFn: async () =>
      (await readData<OpenApiPartnerHistory[] | null>(
        await adminFetch(`${BASE}/${encodeURIComponent(partnerId ?? '')}/histories`),
        '변경 이력을 불러오지 못했습니다.',
      )) ?? [],
  });

// ───────────────────────── 변경 ─────────────────────────

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: openApiPartnersKey });
};

/** 등록 — partnerKey·key 를 BE 가 발급한다. 원문은 응답에서 한 번만. */
export const useCreateOpenApiPartnerMutation = () => {
  const invalidate = useInvalidate();
  return useMutation<IssuedKeys, OpenApiPartnerError, PartnerCreatePayload>({
    mutationFn: async (payload) =>
      readData<IssuedKeys>(await adminFetch(BASE, json('POST', payload)), '제휴사를 등록하지 못했습니다.'),
    // 결과(키 원문)를 mutation 캐시에 남기지 않는다 — reset 즉시 캐시에서 지운다(기본 5분 보관 방지).
    gcTime: 0,
    onSuccess: () => void invalidate(),
  });
};

export const useUpdateOpenApiPartnerMutation = () => {
  const invalidate = useInvalidate();
  return useMutation<OpenApiPartner, OpenApiPartnerError, { partnerId: string; payload: PartnerUpdatePayload }>({
    mutationFn: async ({ partnerId, payload }) =>
      normalize(
        await readData<OpenApiPartner>(
          await adminFetch(`${BASE}/${encodeURIComponent(partnerId)}`, json('PUT', payload)),
          '제휴사를 수정하지 못했습니다.',
        ),
      ),
    onSuccess: () => void invalidate(),
  });
};

/** 키 재발급 — 재발급한 쪽만 원문 1회, 옛 키는 즉시 못 쓴다. */
export const useReissueOpenApiKeysMutation = () => {
  const invalidate = useInvalidate();
  return useMutation<IssuedKeys, OpenApiPartnerError, { partnerId: string; target: KeyTarget; reason: string | null }>({
    mutationFn: async ({ partnerId, target, reason }) =>
      readData<IssuedKeys>(
        await adminFetch(`${BASE}/${encodeURIComponent(partnerId)}/keys/reissue`, json('POST', { target, reason })),
        '키를 재발급하지 못했습니다.',
      ),
    gcTime: 0,
    onSuccess: () => void invalidate(),
  });
};

/** 삭제 — 소프트 삭제(이력 보존), 즉시 호출 불가. 204. */
export const useDeleteOpenApiPartnerMutation = () => {
  const invalidate = useInvalidate();
  return useMutation<void, OpenApiPartnerError, { partnerId: string; reason: string | null }>({
    mutationFn: async ({ partnerId, reason }) => {
      const res = await adminFetch(`${BASE}/${encodeURIComponent(partnerId)}`, json('DELETE', { reason }));
      if (!res.ok) throw await toError(res, '제휴사를 삭제하지 못했습니다.');
    },
    onSuccess: () => void invalidate(),
  });
};
